import React, { useState, useEffect } from 'react'
import SelectField from '../common/SelectField.js';
import { useNavigate, useParams } from 'react-router-dom'
import { DashboardLayout } from '../common/page_component/index.js'
import Loader from "../common/Loader.js"
import Alert from '../common/Alert'
import tripContractService from '../../services/tripContractService'
import { ArrowLeft, Save } from 'lucide-react'
import { validators, inputFilters, useValidation, ValidationMessage } from '../common/Validation.js'
import Button from '../common/Button.js'
import { useTranslation } from 'react-i18next';


const EditTripContract = () => {
  const navigate = useNavigate()
  const { tripId } = useParams()
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    delivery_location: '',
    handling: '',
    parking: '',
    other: '',
    remark: '',
    status: '',
    base_trip_cost: '',
    trip_id: '',
    invoice_no: '',
    contract_party_name: '',
    vehicle_number: ''
  })
  const { errors, validate, validateField } = useValidation()

  const validationRules = {
    delivery_location: (v) => validators.required(v, 'Delivery location'),
  }

  useEffect(() => {
    fetchTripDetails()
  }, [tripId])

  const fetchTripDetails = async () => {
    try {
      setLoading(true)
      const response = await tripContractService.getOne(tripId)
      const trip = response.data.data
      setFormData({
        delivery_location: trip.delivery_location || '',
        handling: trip.handling || '',
        parking: trip.parking || '',
        other: trip.other || '',
        remark: trip.remark || '',
        status: trip.status || 'active',
        base_trip_cost: trip.base_trip_cost || '',
        trip_id: trip.trip_id || '',
        invoice_no: trip.invoice_no || '',
        contract_party_name: trip.contract_party_name || '',
        vehicle_number: trip.vehicle_number || ''
      })
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to load trip contract')
      navigate('/trip-contracts')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    let filtered = value
    if (['handling', 'parking', 'other'].includes(name)) {
      filtered = inputFilters.amount(value)
    }
    setFormData({ ...formData, [name]: filtered })

    if (validationRules[name]) {
      validateField(name, filtered, validationRules[name])
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate(formData, validationRules)) return

    try {
      setSubmitting(true)
      const payload = {
        delivery_location: formData.delivery_location,
        handling: formData.handling ? parseFloat(formData.handling) : 0,
        parking: formData.parking ? parseFloat(formData.parking) : 0,
        other: formData.other ? parseFloat(formData.other) : 0,
        remark: formData.remark,
        status: formData.status
      }

      await tripContractService.update(tripId, payload)
      Alert.success('Trip contract updated successfully!')
      navigate(`/trip-contracts/view/${tripId}`)
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update trip contract')
      console.error(err)
    } finally {
      setSubmitting(false)
    }
  }

  const calculateTotal = () => {
    const base = parseFloat(formData.base_trip_cost || 0)
    const handling = parseFloat(formData.handling || 0)
    const parking = parseFloat(formData.parking || 0)
    const other = parseFloat(formData.other || 0)
    return (base + handling + parking + other).toFixed(2)
  }

  const total = calculateTotal()

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading trip contract..." />
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl">
        {/* Page Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate(`/trip-contracts/view/${tripId}`)}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-secondary">Edit Trip Contract</h1>
            <p className="text-sm text-gray-600 mt-1">Trip ID: {formData.trip_id}</p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-6 space-y-6">
          {/* Read-Only Fields */}
          <div className="bg-blue-50 rounded-lg p-4 space-y-4">
            <h3 className="font-semibold text-gray-900">Contract Information (Read-Only)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Trip ID
                </label>
                <input
                  type="text"
                  value={formData.trip_id}
                  disabled
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-600"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Invoice No
                </label>
                <input
                  type="text"
                  value={formData.invoice_no}
                  disabled
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-600"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contract Party
                </label>
                <input
                  type="text"
                  value={formData.contract_party_name}
                  disabled
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-600"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vehicle
                </label>
                <input
                  type="text"
                  value={formData.vehicle_number}
                  disabled
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-600"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Base Trip Cost
                </label>
                <input
                  type="text"
                  value={parseFloat(formData.base_trip_cost || 0).toFixed(2)}
                  disabled
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-600"
                />
              </div>
            </div>
          </div>

          {/* Editable Fields */}
          <div className="space-y-6">
            <h3 className="font-semibold text-gray-900">Editable Information</h3>

            {/* Delivery Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Delivery Location *
              </label>
              <input
                type="text"
                name="delivery_location"
                value={formData.delivery_location}
                onChange={handleInputChange}
                placeholder="Enter delivery location"
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-400 ${
                  errors.delivery_location ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              <ValidationMessage error={errors.delivery_location} />
            </div>

            {/* Cost Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Handling (Optional)
                </label>
                <input
                  type="text"
                  name="handling"
                  value={formData.handling}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-400 ${
                    errors.handling ? 'border-red-500' : 'border-gray-200'
                  }`}
                />
                <ValidationMessage error={errors.handling} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Parking (Optional)
                </label>
                <input
                  type="text"
                  name="parking"
                  value={formData.parking}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-400 ${
                    errors.parking ? 'border-red-500' : 'border-gray-200'
                  }`}
                />
                <ValidationMessage error={errors.parking} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Other Charges (Optional)
                </label>
                <input
                  type="text"
                  name="other"
                  value={formData.other}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-400 ${
                    errors.other ? 'border-red-500' : 'border-gray-200'
                  }`}
                />
                <ValidationMessage error={errors.other} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <SelectField variant="form"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                >
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="cancelled">Cancelled</option>
                </SelectField>
              </div>
            </div>

            {/* Total Cost Summary */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex justify-between items-center text-lg font-semibold">
                <span>Total Cost:</span>
                <span className="text-orange-600">
                  {parseFloat(total).toLocaleString('en-IN', {
                    style: 'currency',
                    currency: 'INR'
                  })}
                </span>
              </div>
            </div>

            {/* Remark */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Remarks (Optional)
              </label>
              <textarea
                name="remark"
                value={formData.remark}
                onChange={handleInputChange}
                placeholder="Add any additional remarks"
                rows="3"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex gap-4 pt-6">
                    <Button variant="secondary" onClick={() => navigate(`/trip-contracts/view/${tripId}`)} className="flex-1">
                      Cancel
                    </Button>
                    <Button variant="primary" type="submit" loading={submitting} icon={Save} className="flex-1">
                      {submitting ? 'Updating...' : 'Update Trip Contract'}
                    </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  )
}

export default EditTripContract
